import {
  filterAssetSelectorsByChainIds,
  filterValidAssetSelectors
} from '@/helpers/filter/assetSelectors'
import { orpc } from '@/lib/orpc/client'
import { type VaultListInput, vaultListInputSchema } from '@/lib/procedures/vaultList.contract'

import { resolveVaultsChainIds } from './chainIds'
import {
  deriveVaultsVersion,
  type ExploreVaultsFilters,
  type VaultOrderBy,
  VAULTS_PAGE_SIZE
} from './config'

export type VaultListQueryInput = Omit<VaultListInput, 'cursor'>

type VaultListFilters = Omit<ExploreVaultsFilters, 'sorting'>

const finite = (value: number | null) =>
  value != null && Number.isFinite(value) ? value : undefined
const bound = (value: number | null) => {
  const usd = finite(value)
  return usd != null ? Math.max(0, usd) : undefined
}
const apyBound = (percent: number | null) => {
  const value = finite(percent)
  return value != null ? value / 100 : undefined
}

const MAX_SEARCH_LENGTH = 100
const MAX_CURATOR_ID_LENGTH = 100

/**
 * The `vault.list` input for the table's URL filters and sort. Selectors are validated and
 * pruned to the queried chains, which also keeps the request within the bridge's bounds.
 */
export function toVaultListInput(
  filters: VaultListFilters,
  { orderBy, desc }: { orderBy: VaultOrderBy; desc: boolean }
): VaultListQueryInput {
  const chainIds = resolveVaultsChainIds(filters.chains)
  const selectors = (values: string[]) =>
    filterAssetSelectorsByChainIds(filterValidAssetSelectors(values), chainIds)
  const version = deriveVaultsVersion(filters.v1)
  const curatorIds = [
    ...new Set(filters.curators.filter(id => id && id.length <= MAX_CURATOR_ID_LENGTH))
  ]
  return {
    limit: VAULTS_PAGE_SIZE,
    orderBy,
    orderDirection: desc ? 'desc' : 'asc',
    search: filters.search.slice(0, MAX_SEARCH_LENGTH) || undefined,
    chainIds,
    versions: version ? [version] : undefined,
    assets: selectors(filters.assets),
    curatorIds: curatorIds.length ? curatorIds : undefined,
    collateralExposure: selectors(filters.collateralExposure),
    collateralExclusion: selectors(filters.collateralExclusion),
    totalAssetsUsdGte: bound(filters.totalAssetsUsd_gte),
    totalAssetsUsdLte: bound(filters.totalAssetsUsd_lte),
    liquidityUsdGte: bound(filters.liquidityUsd_gte),
    liquidityUsdLte: bound(filters.liquidityUsd_lte),
    netApyGte: apyBound(filters.netApy_gte),
    netApyLte: apyBound(filters.netApy_lte),
    includeFacets: true
  }
}

/**
 * Whether the input fits the contract's bounds. Values are normalized above, so only a selection
 * over the list-size limit fails here, and the table asks to remove filters.
 */
export const isVaultListInputWithinBounds = (input: VaultListQueryInput) =>
  vaultListInputSchema.safeParse(input).success

/** Shared by the server prefetch and the client query, so the hydrated cache entry matches. */
export const vaultListInfiniteOptions = (input: VaultListQueryInput) =>
  orpc.vault.list.infiniteOptions({
    input: (cursor: string | undefined) => ({ ...input, cursor }),
    initialPageParam: undefined,
    getNextPageParam: page => page.nextCursor ?? undefined,
    staleTime: 60 * 1000
  })
